import { hasText } from 'shared/lib/common-util'
import { FieldRequirementResolverContext } from './types'
import { createFieldRequirementGetter, matchServerRequirement } from '../../resolverFactory'
import {
  FieldRequirement, FieldRequirementEnvironment, FieldRequirementResolverMap, forbidden, optional, required, toFieldRequirement,
} from '../../../../../models/fieldRules'
import C0394Validator from '../../../../../../../shared/ncts/p6/conditions/C0394'
import C0671Validator from '../../../../../../../shared/ncts/p6/conditions/C0671'
import NC0035Validator from '../../../../../../../shared/ncts/FI/NC0035'
import NC9012Validator from '../../../../../../../shared/ncts/FI/NC9012'
import C0505Validator from '../../../../../../../shared/ncts/p6/conditions/C0505'
import C0382Validator from '../../../../../../../shared/ncts/p6/conditions/C0382'

const ENS_COUNTRIES_WITH_MANDATORY_LOCATION_OF_GOODS = ['RO', 'PL']

function ruleForActiveBorderTransportMeansEstimatedDateAndTimeOfDeparture(
  env: FieldRequirementEnvironment<FieldRequirementResolverContext>,
): FieldRequirement {
  env.ignoreServerRequirement()

  const { ctx } = env
  const { activeBorderTransportMeansItem } = ctx
  if (!activeBorderTransportMeansItem) return 'required'

  const isRequired = !hasText(activeBorderTransportMeansItem.actualDateAndTimeOfDeparture)

  return isRequired ? 'required' : 'optional'
}

function ruleForActiveBorderTransportMeansActualDateAndTimeOfDeparture(
  env: FieldRequirementEnvironment<FieldRequirementResolverContext>,
): FieldRequirement {
  env.ignoreServerRequirement()

  const { ctx } = env
  const { activeBorderTransportMeansItem } = ctx
  if (!activeBorderTransportMeansItem) {
    return 'required'
  }

  const isRequired = hasText(activeBorderTransportMeansItem.actualDateAndTimeOfDeparture)
  || !hasText(activeBorderTransportMeansItem.estimatedDateAndTimeOfDeparture)

  return isRequired ? 'required' : 'optional'
}

function ruleForLocationOfGoods(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  env.ignoreServerRequirement()

  const { ctx } = env

  if (ctx.locationOfGoods?.typeOfLocation) return 'required'

  if (ENS_COUNTRIES_WITH_MANDATORY_LOCATION_OF_GOODS.includes(ctx.country ?? '')) {
    return 'required'
  }

  return 'optional'
}

function ruleForLocationOfGoodsAuthorisationNumber(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  env.ignoreServerRequirement()

  const { ctx } = env

  return toFieldRequirement(C0394Validator.validateAuthorisationNumberRequirement(ctx))
}

function ruleForLocationOfGoodsAdditionalIdentifier(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  env.ignoreServerRequirement()

  const { ctx } = env

  if (ctx.country === 'FI') return 'forbidden'
  return toFieldRequirement(C0671Validator.validateAdditionalIdentifierRequirement(ctx))
}

function ruleForLocationOfGoodsEconomicOperatorIdentificationNumber(
  env: FieldRequirementEnvironment<FieldRequirementResolverContext>,
): FieldRequirement {
  const { ctx } = env

  if (ctx.country === 'FI') {
    return toFieldRequirement(NC0035Validator.validateEconomicOperatorRequirement(ctx))
  }

  return toFieldRequirement(C0394Validator.validateEconomicOperatorRequirement(ctx))
}

function ruleForLocationOfGoodsGnss(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  env.ignoreServerRequirement()

  const { ctx } = env

  return toFieldRequirement(C0394Validator.validateGnssRequirement(ctx))
}

function ruleForLocationOfGoodsQualifierOfIdentification(
  env: FieldRequirementEnvironment<FieldRequirementResolverContext>,
): FieldRequirement {
  const { ctx } = env

  if (ctx.country === 'FI') return 'forbidden'

  return ctx.locationOfGoods?.typeOfLocation ? 'required' : 'forbidden'
}

function ruleForLocationOfGoodsContactPerson(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  const qualifierOfIdentificationRequirement = env.getFieldRequirement('locationOfGoods.qualifierOfIdentification') ?? 'optional'
  if (qualifierOfIdentificationRequirement === 'forbidden') return 'forbidden'

  return toFieldRequirement(C0394Validator.validateContactPersonRequirement(ctx))
}

function ruleForLocationOfGoodsAddress(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  env.ignoreServerRequirement()

  const { ctx } = env

  if (ctx.country === 'FI') {
    return toFieldRequirement(NC9012Validator.validateAddressRequirement(ctx))
  }

  const qualifierOfIdentificationRequirement = env.getFieldRequirement('locationOfGoods.qualifierOfIdentification') ?? 'optional'
  if (qualifierOfIdentificationRequirement === 'forbidden') return 'forbidden'

  return toFieldRequirement(C0394Validator.validateAddressRequirement(ctx))
}

function ruleForLocationOfGoodsAddressPart(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  if (!ctx.locationOfGoods?.address) return 'forbidden'

  return ruleForLocationOfGoodsAddress(env)
}

function ruleForLocationOfGoodsAddressPostcode(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  if (!ctx.locationOfGoods?.address) return 'forbidden'

  const parentRequirement = ruleForLocationOfGoodsAddress(env)
  if (parentRequirement === 'forbidden') return 'forbidden'

  return toFieldRequirement(C0505Validator.validatePostcodeRequirement(ctx?.locationOfGoods?.address?.country))
}

function ruleForLocationOfGoodPostcodeAddress(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  env.ignoreServerRequirement()

  const { ctx } = env

  const qualifierOfIdentificationRequirement = env.getFieldRequirement('locationOfGoods.qualifierOfIdentification') ?? 'optional'
  if (qualifierOfIdentificationRequirement === 'forbidden') return 'forbidden'

  return toFieldRequirement(C0394Validator.validatePostCodeAddressRequirement(ctx))
}

function ruleForLocationOfGoodPostcodeAddressHouseNumber(
  env: FieldRequirementEnvironment<FieldRequirementResolverContext>,
): FieldRequirement {
  const { ctx } = env

  const parentRequirement = ruleForLocationOfGoodPostcodeAddress(env)
  if (parentRequirement === 'forbidden') return 'forbidden'

  return toFieldRequirement(C0382Validator.validateHouseNumberRequirement(ctx))
}

function ruleForLocationOfGoodsReferenceNumber(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  if (ctx.country === 'FI') return 'forbidden'

  const qualifierOfIdentificationRequirement = env.getFieldRequirement('locationOfGoods.qualifierOfIdentification') ?? 'optional'
  if (qualifierOfIdentificationRequirement === 'forbidden') return 'forbidden'

  return toFieldRequirement(C0394Validator.validateCustomsOfficeRequirement(ctx))
}

function ruleForLocationOfGoodsTypeOfLocation(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  return ctx.locationOfGoods?.typeOfLocation ? 'required' : 'optional'
}

function ruleForLocationOfGoodsUnLocode(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  env.ignoreServerRequirement()

  const { ctx } = env

  if (ctx.country === 'FI') return 'forbidden'

  const qualifierOfIdentificationRequirement = env.getFieldRequirement('locationOfGoods.qualifierOfIdentification') ?? 'optional'
  if (qualifierOfIdentificationRequirement === 'forbidden') return 'forbidden'

  return toFieldRequirement(C0394Validator.validateUnLocodeRequirement(ctx))
}

const fieldRequirementResolvers: FieldRequirementResolverMap<FieldRequirementResolverContext> = {
  activeBorderTransportMeans: optional,
  'activeBorderTransportMeans.*.conveyanceReferenceNumber': forbidden,
  'activeBorderTransportMeans.*.customsOfficeAtBorderReferenceNumber': forbidden,
  'activeBorderTransportMeans.*.identificationNumber': required,
  'activeBorderTransportMeans.*.nationality': required,
  'activeBorderTransportMeans.*.typeOfIdentification': required,
  'activeBorderTransportMeans.*.typeOfMeansOfTransport': required,
  'activeBorderTransportMeans.*.estimatedDateAndTimeOfDeparture': ruleForActiveBorderTransportMeansEstimatedDateAndTimeOfDeparture,
  'activeBorderTransportMeans.*.actualDateAndTimeOfDeparture': ruleForActiveBorderTransportMeansActualDateAndTimeOfDeparture,
  'activeBorderTransportMeans.*.estimatedDateAndTimeOfArrival': required,
  'activeBorderTransportMeans.*.countriesOfRouting': required,
  'activeBorderTransportMeans.*.countriesOfRouting.*.country': required,
  additionalDeclarationType: forbidden,
  additionalInformation: forbidden,
  additionalReference: forbidden,
  'carrier.address.city': required,
  'carrier.address.street': required,
  'carrier.address.streetNumber': required,
  'carrier.address.country': required,
  'carrier.address.postcode': required,
  'carrier.contactPerson.email': required,
  'carrier.identificationNumber': required,
  'carrier.name': required,
  'consignee.address.city': required,
  'consignee.address.addressLine': forbidden,
  'consignee.address.street': required,
  'consignee.address.streetNumber': required,
  'consignee.address.country': required,
  'consignee.address.postcode': required,
  'consignee.contactPerson.email': required,
  'consignee.name': required,
  'consignee.typeOfPerson': required,
  'consignor.address.city': required,
  'consignor.address.addressLine': forbidden,
  'consignor.address.street': required,
  'consignor.address.streetNumber': required,
  'consignor.address.country': required,
  'consignor.address.postcode': required,
  'consignor.name': required,
  'consignor.typeOfPerson': required,
  countryOfDispatch: forbidden,
  departureCustomsOffice: required,
  departureTransportMeans: forbidden,
  'departureTransportMeans.*.typeOfIdentification': required,
  'departureTransportMeans.*.identificationNumber': required,
  'departureTransportMeans.*.nationality': required,
  destinationCustomsOffice: required,
  inlandModeOfTransport: forbidden,
  grossMass: required,
  guarantees: forbidden,
  limitDate: forbidden,
  placeOfAcceptance: required,
  'placeOfAcceptance.country': required,
  'placeOfAcceptance.location': required,
  'placeOfAcceptance.unLocode': forbidden,
  placeOfDelivery: required,
  'placeOfDelivery.country': required,
  'placeOfDelivery.location': required,
  'placeOfDelivery.unLocode': forbidden,
  placeOfLoading: required,
  'placeOfLoading.location': required,
  'placeOfLoading.country': required,
  'placeOfLoading.unLocode': forbidden,
  security: required,
  'supportingDocument.*.documentType': required,
  'supportingDocument.*.referenceNumber': required,
  transportChargesMethodOfPayment: required,
  transportDocument: required,
  'transportDocument.*.complementOfInformation': optional,
  'transportDocument.*.documentLineNumber': optional,
  'transportDocument.*.documentType': required,
  'transportDocument.*.files': optional,
  'transportDocument.*.procedureType': optional,
  'transportDocument.*.referenceNumber': required,
  'transportDocument.*.transportDocumentReferenceNumber': optional,
  locationOfGoods: ruleForLocationOfGoods,
  'locationOfGoods.authorisationNumber': ruleForLocationOfGoodsAuthorisationNumber,
  'locationOfGoods.additionalIdentifier': ruleForLocationOfGoodsAdditionalIdentifier,
  'locationOfGoods.address': ruleForLocationOfGoodsAddress,
  'locationOfGoods.address.addressLine': ruleForLocationOfGoodsAddressPart,
  'locationOfGoods.address.city': ruleForLocationOfGoodsAddressPart,
  'locationOfGoods.address.country': ruleForLocationOfGoodsAddressPart,
  'locationOfGoods.address.postcode': ruleForLocationOfGoodsAddressPostcode,
  'locationOfGoods.address.street': forbidden,
  'locationOfGoods.address.streetNumber': forbidden,
  'locationOfGoods.contactPerson': ruleForLocationOfGoodsContactPerson,
  'locationOfGoods.economicOperatorIdentificationNumber': ruleForLocationOfGoodsEconomicOperatorIdentificationNumber,
  'locationOfGoods.gnss': ruleForLocationOfGoodsGnss,
  'locationOfGoods.postcodeAddress': ruleForLocationOfGoodPostcodeAddress,
  'locationOfGoods.postcodeAddress.houseNumber': ruleForLocationOfGoodPostcodeAddressHouseNumber,
  'locationOfGoods.qualifierOfIdentification': ruleForLocationOfGoodsQualifierOfIdentification,
  'locationOfGoods.referenceNumber': ruleForLocationOfGoodsReferenceNumber,
  'locationOfGoods.typeOfLocation': ruleForLocationOfGoodsTypeOfLocation,
  'locationOfGoods.unLocode': ruleForLocationOfGoodsUnLocode,

}

function getServerRequirementForPath(
  path: string,
  ctx: FieldRequirementResolverContext,
) {
  return matchServerRequirement(ctx.serverValidationResult?.fieldRequirements, path)
}

const getFieldRequirement = createFieldRequirementGetter<FieldRequirementResolverContext>(
  fieldRequirementResolvers,
  getServerRequirementForPath,
)

export default getFieldRequirement
